import { audienceHasChoice, type AudiencePreview, type AudienceSpec } from '@up2d8/contracts';

/** Wat het voorbeeld toont zolang er nog niets gekozen is: nul, zonder de server te vragen. */
const NOG_NIETS_GEKOZEN: AudiencePreview = {
  count: 0,
  description: '',
  sample: [],
  sampleWithheld: false,
};

/**
 * Het voorbeeld van een doelgroep, of nul als er nog niets te tellen valt.
 *
 * Bij "Kiezen" begint de doelgroep als een lege filter. Die ging naar `/audience/preview`, de server
 * weigerde ze (400) en het scherm zei "Het aantal is nu niet op te halen", terwijl er gewoon nog
 * niets gekozen was (testrapport 28 okt.). Nul is hier het eerlijke antwoord, en houdt ook
 * "Publiceren" grijs.
 */
export async function previewOrNothing<T>(
  spec: AudienceSpec,
  fetch: () => Promise<T>,
): Promise<T | AudiencePreview> {
  if (!audienceHasChoice(spec)) return NOG_NIETS_GEKOZEN;
  return fetch();
}

/**
 * Is dit "Kiezen" zonder dat er iets gekozen is?
 *
 * Zo'n doelgroep kan niet bewaard worden: de server weigert een lege filter bewust
 * (`audienceSpecSchema`, geen sluipweg naar "iedereen"). Een checklist met alleen "Ook toewijzen
 * aan" gaf daardoor een 400 met "Validation failed" in een toast, en het paneel bleef zonder uitleg
 * open (hertest 30 okt.). Daarom toont elke doelgroepkiezer bij een lege keuze een hint, en staat
 * elke knop die de doelgroep naar de server stuurt grijs zolang dit `true` is.
 *
 * `null` (een scherm dat de doelgroep niet aanraakt, zoals de instellingen van een formulier) telt
 * niet als leeg: er gaat dan geen doelgroep mee.
 */
export function audienceIncomplete(spec: AudienceSpec | null | undefined): boolean {
  return spec != null && !audienceHasChoice(spec);
}

/**
 * Wat er na het aantal komt: " — Ploeg Nacht" (met `end` erachter), of **niets**.
 *
 * Zonder keuze is de beschrijving leeg, en dan stond er "0 medewerkers — ." met een los streepje
 * en een los punt (hertest 29 okt., noodkanaal). Een scheidingsteken hoort alleen bij iets dat
 * erna komt.
 */
export function describedAudience(description: string | null | undefined, end = ''): string {
  const text = description?.trim() ?? '';
  return text === '' ? '' : ` — ${text}${end}`;
}
